/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import React, {useState} from 'react';
import {Button} from '@mui/material';
import {HnaRequest, HnaZone} from "../../../shared/entities/hnaRequest";
import {FileCopy} from "@mui/icons-material";
import BeamTooltip from '../../common/beam/BeamTooltip';
import {formatDottedDateYYYYMMDD} from "../../../utils/DateTimeUtils";

interface HnaRequestToClipboardButtonProps {
  hnaRequest: HnaRequest;
}

const HnaRequestToClipboardButton: React.FC<HnaRequestToClipboardButtonProps> = ({ hnaRequest }) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(formatHnaRequestText(hnaRequest)).then(() => {
      setTooltipOpen(true);
      setTimeout(() => setTooltipOpen(false), 2000);
    });
  };

  return (
    <BeamTooltip title="Copied!" open={tooltipOpen} disableHoverListener>
      <Button
        startIcon={<FileCopy />}
        onClick={handleCopyToClipboard}
        variant="outlined"
        color="secondary"
        sx={{
          borderRadius: '1000px',
          color: '#ffffff',
          lineHeight: '24px',
          letterSpacing: 0,
          textAlign: 'center',
        }}
      >
        Copy Request to Clipboard
      </Button>
    </BeamTooltip>
  );
};

const formatHnaRequestText = (hnaRequest: HnaRequest) => {
  return `Please find HNA Request below for mission number ${hnaRequest.missionNumber}

REQUESTOR INFORMATION 
Name: ${hnaRequest.requestorName}
Organization: ${hnaRequest.requestorOrgName}

MISSION INFORMATION
Mission Number: ${hnaRequest.missionNumber}
Mission Start Date: ${formatDottedDateYYYYMMDD(hnaRequest.missionStartDate)}
Mission End Date: ${formatDottedDateYYYYMMDD(hnaRequest.missionEndDate)}
Number of Aircraft: ${hnaRequest.tailNumbers?.length || 0}
Tail Number(s): ${hnaRequest.tailNumbers}
Antenna Type: ${hnaRequest.antennaType}
APACS: ${hnaRequest.apacsId}
Additional Information: ${hnaRequest.additionalInfo ?? ''}

NETWORK INFORMATION
${hnaRequest.hnaZones
  .map((hnaZone: HnaZone) => {
    let regionLine = `${hnaZone.country} - ${hnaZone.networks
      .map(network => `${network.network}`)
      .join(', ')}`;
    return regionLine;
  })
  .join('\n')}

Request generated via Unified Portal on ${formatDottedDateYYYYMMDD(hnaRequest.requestedAt)}
`;
};

export default HnaRequestToClipboardButton;
